/* eslint-disable react-refresh/only-export-components */
import { Box, Flex, FormControl, Text, Avatar } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { FormCustomLabel } from '../../components';
import { renderErrors } from '@/ui/util';
import EditorCustomVariables from '../FormEditor/EditorCustomVariables';
import { useField } from 'formik';
import { useLazyUsersQuery } from '@/store/query/account-invite';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { getInvalidVariableAlertConfig } from '@/utils/getInvalidVariableAlertConfig';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { filterSubjectEmailVariables } from '@/utils/filterEmailVariables';
import { components } from 'react-select';
import { KText } from '@/ui-library/components/text';
const labelStyles = {
  fontSize: '14px',
  fontWeight: 'semiBold',
  minWidth: '50px',
  marginBottom: '0px',
};

const containerStyles = {
  paddingLeft: '16px',
  paddingRight: '16px',
  margin: '0px',
  borderColor: 'border-color',
};

const selectProps = {
  containerStyles: {
    width: '100%',
    borderBottom: '1px solid',
    ...containerStyles,
  },
  labelStyles,
  formControlContainerStyles: {
    flexDir: 'row',
    gap: '10px',
    alignItems: 'center',
  },
  menuShouldScrollIntoView: true,
  maxMenuHeight: 200,
  newStyles: {
    control: (provided) => ({
      ...provided,
      border: 'none',
      boxShadow: 'none',
      '&:hover': {
        border: 'none',
        boxShadow: 'none',
        cursor: 'pointer',
      },
      '&:focus-within': {
        border: 'none',
        boxShadow: 'none',
      },
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: '200px',
      overflowY: 'auto',
    }),
  },
};

const editorContainerStyles = {
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingBottom: '16px',
  margin: '0px',
  borderColor: 'border-color',
};

export const useEmailField = ({
  formRef,
  templateData = undefined,
  valueKey = 'email',
  labelKey = 'name',
  ccBccValueKey = undefined,
  disableFrom = false,
  bodyLabel = 'Body',
  showBodyLabel = true,
  showFromField = true,
  variables = undefined,
  defaultAllowedFromUsers = undefined,
  replaceContent = true,
  isBodyRequired = true,
  getEditorInstance = undefined,
  userEmailType: _userEmailType = 'users',
  jobId: _jobId = undefined,
  isReplyMode = false,
  disableConfig = undefined,
  showInvalidVariableAlert = true,
  enableSurveySelection = false,
  pluginsConfig = [
    'link',
    'image',
    'attachment',
    ...(enableSurveySelection ? ['survey'] : []),
  ],
  hideToolbar = false,
  wrapperProps = {},
  toAddress = [
    //sample options, once BE is done, will remove it
    // {
    //   email: 'test@gmail.com',
    //   name: 'test',
    //   avatar: null,
    //   isPrimaryEmail: true,
    // },
    // {
    //   email: 'test22@gmail.com',
    //   name: 'two test',
    //   avatar: null,
    //   isPrimaryEmail: false,
    // },
  ],
  containerPropStyles = {},
  editorHeight = 0,
  templateIsPreview = false,
  previewProps = {},
  toolBarProps = {},
  subjectMaxLength = 150,
  ToAddressMultiSelect = false,
  showVariablesOnRight = false,
  onSurveySelect = undefined,
  onSurveyRemove = undefined,
  onAttachmentRemove = undefined,
  showFromMandatoryIcon = true,
  showToMandatoryIcon = true,
  enableFontSize = false,
  enableFontFamily = false,
  // Overridable subject/body field names so a form can render more than one composer.
  subjectFieldName = 'subject',
  bodyFieldName = 'body',
}) => {
  const { body: bodyVal, subject: subjectVal } = templateData || {};
  const allUsers = useSelector(selectUsers);

  const { fromUsers, isFromUsersLoading, feSharedEmails } = useGetAllUsers();

  const [getUsers, { data: allowedFromUsers = [], isLoading: isUsersLoading }] =
    useLazyUsersQuery();
  const variablesListArr = Object.entries(variables || {}).flatMap(
    ([, values = []]) => values
  );

  const finalCcBccValueKey = ccBccValueKey || valueKey;

  useEffect(() => {
    //todo: for now use all users scope
    // if (userEmailType === 'users') {
    if (showFromField && !defaultAllowedFromUsers && !feSharedEmails) {
      getUsers({
        params: {
          scope: 'email_delegatable',
        },
      });
    }
    // } else {
    //   getHiringTeamUsers({
    //     users: true,
    //     jobId: jobId || jobIdFromParams,
    //     params: {
    //       scope: 'email_delegatable',
    //     },
    //   });
    // }
  }, [getUsers, showFromField]); // eslint-disable-line react-hooks/exhaustive-deps

  const [emailFields, setEmailFields] = useState({
    isCc: false,
    isBcc: false,
  });

  const { isBcc, isCc } = emailFields;

  const {
    // from: fromVal,
    cc: ccVal = [],
    bcc: bccVal = [],
  } = formRef?.current?.values || {};
  const subject = formRef?.current?.values?.[subjectFieldName];
  const body = formRef?.current?.values?.[bodyFieldName];

  const editorMinHeight = editorHeight + 'px' || '350px';
  const editorHeightUpdated = editorHeight - 80 + 'px' || '300px';

  useEffect(() => {
    if (emailFields?.isCc && emailFields?.isBcc) return;
    if (formRef?.current?.values?.cc?.length) {
      setEmailFields((prev) => ({ ...prev, isCc: true }));
    }
    if (formRef?.current?.values?.bcc?.length) {
      setEmailFields((prev) => ({ ...prev, isBcc: true }));
    }
  }, [ccVal, bccVal]); // eslint-disable-line react-hooks/exhaustive-deps

  /***  todo: we can remove these once shared emails is verified  */
  const finalFromUsers = feSharedEmails
    ? fromUsers
    : defaultAllowedFromUsers || allowedFromUsers;
  const finalFromLoading = feSharedEmails ? isFromUsersLoading : isUsersLoading;
  /** */

  // Filter out sender.email_signature from sender variables
  const filteredVariables = filterSubjectEmailVariables(variables);

  const initialFields: Array<Record<string, unknown>> = [
    {
      name: 'from',
      type: 'select',
      label: 'From',
      showErrorStyles: true,
      showMandatoryIcon: showFromMandatoryIcon,
      isMulti: false,
      disabled: disableFrom,
      valueKey,
      labelKey,
      options: finalFromUsers,
      isLoading: finalFromLoading,
      customFormatLabel: CustomLabel,
      components: { Option: CustomSelectOption },
      showAfterContent: true,
      AfterContentsComp: () => {
        return (
          <Flex gap="0.5em" position="absolute" right="65px" top="14px">
            {!isCc && (
              <Text
                size="sm-medium"
                color="ats-primary"
                cursor={disableFrom ? 'normal' : 'pointer'}
                opacity={disableFrom ? 0.5 : 1}
                onClick={() =>
                  !disableFrom &&
                  setEmailFields((prev) => ({ ...prev, isCc: true }))
                }
                data-test-id="enable-cc-button"
              >
                CC
              </Text>
            )}
            {!isBcc && (
              <Text
                size="sm-medium"
                color="ats-primary"
                cursor={disableFrom ? 'normal' : 'pointer'}
                opacity={disableFrom ? 0.5 : 1}
                onClick={() =>
                  !disableFrom &&
                  setEmailFields((prev) => ({ ...prev, isBcc: true }))
                }
                data-test-id="enable-bcc-button"
              >
                BCC
              </Text>
            )}
          </Flex>
        );
      },
      customOnChange: (option) => {
        formRef?.current?.setFieldValue('fromUserObject', option);
      },
      // isRequired: true,
      // validate: (value, item) => {
      //   return value || item?.isRequired === false
      //     ? undefined
      //     : `${item?.label} is required`;
      // },
      ...selectProps,
      newStyles: {
        ...selectProps.newStyles,
        dropdownIndicator: (provided) => ({
          ...provided,
          padding: '0',
        }),
        singleValue: (provided) => ({
          ...provided,
          maxWidth: 'calc(100% - 130px)',
        }),
      },
      containerStyles: {
        width: '100%',
        borderBottom: '1px solid',
        bg: templateIsPreview ? 'secondary-25' : 'inherit',
        ...containerStyles,
      },
    },
  ];

  if (toAddress?.length > 0) {
    initialFields.push({
      // change name once BE is done
      name: 'toAddress',
      type: 'select',
      label: 'To',
      showErrorStyles: true,
      showMandatoryIcon: showToMandatoryIcon,
      isMulti: ToAddressMultiSelect,
      options: toAddress,
      valueKey,
      labelKey,
      customFormatLabel: CustomLabel,
      components: { Option: CustomSelectOption },
      ...selectProps,
    });
  }

  const midFields = [];

  const isSubjectDisabled = disableConfig?.subject?.disabled;

  const subjectInfoBanner = {
    type: 'custom',
    Field: () => {
      if (!isSubjectDisabled) return null;

      return (
        <Box
          bg="secondary-50"
          border="1px solid"
          borderColor="secondary-200"
          borderRadius="6px"
          p="12px 16px"
          mb="16px"
        >
          <KText size="sm-regular">
            <KText as="span" size="sm-semi-bold">
              Note:{' '}
            </KText>
            Subject lines are not applicable for calendar invites
          </KText>
        </Box>
      );
    },
  };

  const subjectField =
    !isReplyMode && !isSubjectDisabled
      ? {
          name: subjectFieldName,
          label: 'Subject',
          // Explicit placeholder — the editor otherwise defaults to "Enter <field name>",
          // which leaks the raw name for renamed fields (e.g. company_email_subject).
          placeholder: 'Enter subject',
          type: 'variableInput',
          maxLength: subjectMaxLength,
          showErrorStyles: true,
          showMandatoryIcon: true,
          disableHoverBg: true,
          content: subjectVal,
          containerStyles: {
            width: '100%',
            borderTopRadius: !showFromField && '6px',
            border: !showFromField && '1px solid',
            borderBottom: '1px solid',
            bg: templateIsPreview ? 'secondary-25' : 'inherit',
            ...containerStyles,
          },
          labelStyles,
          style: { border: 'none', _hover: {}, _focus: {} },
          formControlContainerStyles: {
            flexDir: 'row',
            gap: '10px',
            alignItems: 'center',
          },
          formFieldContainerStyles: {
            overflow: 'hidden',
          },
          editorContainerStyles: {
            border: 'none',
            _hover: {},
            _focus: {},
          },
          enableCustomVariables: true,
          customVariableList: filteredVariables,
          templateIsPreview,
          previewProps: previewProps,
          CustomRightElement: () => {
            const [, { value = '' }, { setValue }] = useField(subjectFieldName);
            const customOnClick = (val) => {
              const newVal = value + ' ' + val;
              setValue(newVal);
            };

            if (!variables) return null;

            return (
              <Box mt={'0px'}>
                <EditorCustomVariables
                  config={variables}
                  customOnClick={customOnClick}
                  wrapperProps={{ pt: '0px' }}
                />
              </Box>
            );
          },
        }
      : null;

  const lastFields = [
    isSubjectDisabled ? subjectInfoBanner : null,
    subjectField,
    {
      name: bodyFieldName,
      type: 'editor',
      pluginsConfig: pluginsConfig,
      enableFontSize,
      enableFontFamily,
      enableCustomVariables: true,
      content: bodyVal,
      customVariableList: variables,
      disableConfig,
      hideToolbar,
      showVariablesOnRight,
      containerStyles: {
        width: '100%',
        border: showFromField ? 'none' : '1px solid',
        borderBottomRadius: showFromField ? 'none' : '6px',
        borderTop: isSubjectDisabled ? '1px solid' : 'none',
        borderTopRadius: isSubjectDisabled && !showFromField ? '6px' : 'none',
        ...editorContainerStyles,
        ...containerPropStyles,
      },
      labelStyles,
      editorContainerStyles: {
        border: 'none',
        minHeight: editorMinHeight,
      },
      getEditorInstance,
      backgroundColor: 'red',
      editorWraperStyles: {
        padding: '0px',
        paddingTop: '10px',
        minHeight: editorHeight ? `${editorHeight - 50}px` : '100%',
        height: '100%',
      },
      editorStyles: { padding: '0px', height: editorHeightUpdated },
      toolbarWrapperStyles: { marginLeft: '-10px' },
      replaceContent,
      previewProps,
      toolBarProps,
      onSurveySelect,
      onSurveyRemove,
      onAttachmentRemove,
      initialSurveyId: templateData?.survey?.value ?? null,
    },
  ].filter(Boolean);

  const bodyLabelConfig = {
    type: 'custom',
    Field: () => {
      const { submitCount } = formRef?.current || {};

      // Determine if subject or body are missing based on submit count
      const subjectError = !isSubjectDisabled && !subject && submitCount > 0;
      const bodyError = isBodyRequired && !body && submitCount > 0;

      const errorMessage =
        subjectError && bodyError
          ? 'Subject and Email content are required'
          : subjectError
            ? 'Subject is required'
            : bodyError
              ? 'Email content is required'
              : '';

      return (
        <FormControl isRequired>
          {showBodyLabel && <FormCustomLabel item={{ label: bodyLabel }} />}
          {/* Show error message if applicable */}
          {errorMessage && <Box>{renderErrors('', errorMessage)}</Box>}
        </FormControl>
      );
    },
  };

  const invalidVariableAlertConfig = getInvalidVariableAlertConfig({
    variablesListArr,
    isBodySubject: true,
    wrapperProps,
  });

  if (emailFields?.isCc || ccVal?.length) {
    midFields.push({
      id: 'cc',
      name: 'cc',
      label: 'CC',
      valueKey: finalCcBccValueKey,
      labelKey,
      disabled: disableFrom,
      isMulti: true,
      type: 'select',
      customFormatLabel: CustomLabel,
      components: {
        Option: CustomSelectOption,
        MultiValueLabel: CustomMultiValueLabel,
      },
      options: allUsers,
      ...selectProps,
      containerStyles: {
        ...selectProps.containerStyles,
        bg: templateIsPreview ? 'secondary-25' : 'inherit',
      },
    });
  }

  if (emailFields?.isBcc || bccVal?.length) {
    midFields.push({
      id: 'bcc',
      name: 'bcc',
      label: 'BCC',
      isMulti: true,
      type: 'select',
      defaultValue: bccVal,
      valueKey: finalCcBccValueKey,
      labelKey,
      disabled: disableFrom,
      customFormatLabel: CustomLabel,
      components: {
        Option: CustomSelectOption,
        MultiValueLabel: CustomMultiValueLabel,
      },
      options: allUsers,
      ...selectProps,
      containerStyles: {
        ...selectProps.containerStyles,
        bg: templateIsPreview ? 'secondary-25' : 'inherit',
      },
    });
  }

  // Combine all fields and return
  const fieldConfig = [];
  if (showFromField) {
    fieldConfig.push(...initialFields);
  } else if (bodyLabel) {
    fieldConfig.push(bodyLabelConfig);
  }
  fieldConfig.push(...midFields, ...lastFields);
  if (variables && showInvalidVariableAlert)
    fieldConfig.push(invalidVariableAlertConfig);
  return fieldConfig;
};

const CustomSelectOption = ({ data, ...props }) => {
  const { name, email, avatar, isPrimaryEmail } = data || {};

  return (
    <components.Option {...props}>
      <Flex gap="5px" alignItems="center">
        <Avatar
          h="20px"
          w="20px"
          borderRadius="6px"
          size="xs"
          name={name}
          src={avatar}
        />
        <Text fontSize="sm">
          {name}
          {email && ` (${email})`}
        </Text>
        {isPrimaryEmail && (
          <Text
            size="sm-medium"
            bgColor="ats-tertiary"
            color="ats-primary"
            p="4px 8px"
            m="4px"
            borderRadius="sm"
          >
            Primary
          </Text>
        )}
      </Flex>
    </components.Option>
  );
};

const CustomMultiValueLabel = ({ data, ...props }) => {
  const { name, email, avatar } = data || {};
  const displayText = email ? `${name} (${email})` : name;

  return (
    <components.MultiValueLabel {...props}>
      <Flex gap="5px" alignItems="center">
        <Avatar
          h="20px"
          w="20px"
          borderRadius="6px"
          size="xs"
          name={name}
          src={avatar}
        />
        <Text fontSize="sm">{displayText}</Text>
      </Flex>
    </components.MultiValueLabel>
  );
};

export const CustomLabel = (option) => {
  const { name, email, avatar, isPrimaryEmail } = option;
  const fullText = email ? `${name} (${email})` : name;

  return (
    <Flex gap="5px" alignItems="center" w="100%">
      <Avatar
        h="20px"
        w="20px"
        borderRadius="6px"
        size="xs"
        name={name}
        src={avatar}
        flexShrink={0}
      />
      <KText
        fontSize="sm"
        isTruncated
        flex="1"
        minW={0}
        enableTooltip={true}
        tooltipText={fullText}
      >
        {name}
        {email && ` (${email})`}
      </KText>
      {isPrimaryEmail && (
        <KText
          size="sm-medium"
          bgColor="ats-tertiary"
          color="ats-primary"
          p="4px 8px"
          m="4px"
          borderRadius="sm"
          flexShrink={0}
        >
          Primary
        </KText>
      )}
    </Flex>
  );
};
